import { zipRecordPermissions } from './recordPermissions'
import type { FgaAssignableRelation, FgaComputedRelation } from './generated/model'
import type { GrantFixedLevel } from './grantFixedSets'

/**
 * What a caller may do to one Insights dashboard: its view, edit, delete and organisation-access verbs, each paired with the camelCase key
 * it is reported under. The model caps each with the organisation's Insights capability.
 */
export const INSIGHTS_DASHBOARD_RECORD_PERMISSIONS = [
  ['canView', 'can_view'],
  ['canEdit', 'can_edit'],
  ['canDelete', 'can_delete'],
  ['canManageOrgAccess', 'can_manage_org_access'],
] as const satisfies readonly (readonly [string, FgaComputedRelation<'insights_dashboard'>])[]

export type InsightsDashboardRecordPermissionKey = (typeof INSIGHTS_DASHBOARD_RECORD_PERMISSIONS)[number][0]

export type InsightsDashboardRecordPermissions = Record<InsightsDashboardRecordPermissionKey, boolean>

/** The answer for a caller with no access, and the seed every result is built from. */
export const NO_INSIGHTS_DASHBOARD_RECORD_PERMISSIONS: InsightsDashboardRecordPermissions = {
  canView: false,
  canEdit: false,
  canDelete: false,
  canManageOrgAccess: false,
}

export const toInsightsDashboardRecordPermissions = (allowed: readonly boolean[]): InsightsDashboardRecordPermissions =>
  zipRecordPermissions(
    'insights_dashboard',
    INSIGHTS_DASHBOARD_RECORD_PERMISSIONS,
    NO_INSIGHTS_DASHBOARD_RECORD_PERMISSIONS,
    allowed
  )

/** The relation a dashboard share at each fixed level is written as. Owner is the dialog's "Can edit"; delete stays the owner's. */
export const INSIGHTS_DASHBOARD_SHARE_RELATION = {
  viewer: 'viewer',
  owner: 'editor',
} as const satisfies Record<GrantFixedLevel, FgaAssignableRelation<'insights_dashboard'>>
